import { useState } from "react";
import { motion } from "framer-motion";
import { Play, MapPin, Mail, Phone, Send, ArrowUpRight, Loader2, X, Sparkles } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { useContent, resolveSrc } from "@/content/ContentContext";
import { useT } from "@/content/i18n";

const list = (v) => (Array.isArray(v) ? v : []);
const ease = [0.22, 1, 0.36, 1];

const toEmbed = (url = "") => {
  const yt = url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=1&rel=0`;
  const vm = url.match(/vimeo\.com\/(?:video\/)?(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}?autoplay=1`;
  return url;
};

const spotifyEmbed = (url = "") => {
  const m = url.match(/open\.spotify\.com\/(?:intl-\w+\/)?(?:embed\/)?(album|track|playlist|artist)\/([\w]+)/);
  return m ? `https://open.spotify.com/embed/${m[1]}/${m[2]}?theme=0` : null;
};

const Eyebrow = ({ children }) => <p className="mb-3 font-mono text-[11px] uppercase tracking-[0.25em] text-[#E6B877]">{children}</p>;

const Bar = ({ value }) => (
  <div className="h-1.5 w-full overflow-hidden rounded-full bg-[#F7F4EE]/10">
    <motion.div className="h-full rounded-full bg-gradient-to-r from-[#C85A32] to-[#E69C24]" initial={{ width: 0 }}
      animate={{ width: `${Math.max(0, Math.min(100, Number(value) || 0))}%` }} transition={{ duration: 1.1, delay: 0.25, ease }} />
  </div>
);

const Empty = () => {
  const t = useT();
  return <p className="text-sm text-[#C5BDB3]">{t("empty")}</p>;
};

const Timeline = ({ data }) => {
  const items = list(data?.items);
  if (!items.length) return <Empty />;
  return (
    <ol className="relative space-y-7 border-l border-[#F7F4EE]/12 pl-6" data-testid="section-timeline">
      {items.map((it, i) => (
        <li key={i} className="relative">
          <span className="absolute -left-[29px] top-1.5 h-2.5 w-2.5 rounded-full bg-[#E69C24] ring-4 ring-[#E69C24]/15" />
          {it.period && <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#E6B877]">{it.period}</p>}
          <h3 className="mt-1 font-display text-lg text-[#F7F4EE]">{it.title}</h3>
          <p className="text-sm text-[#D9CFC2]">{[it.subtitle, it.place].filter(Boolean).join(" · ")}</p>
          {it.description && <p className="mt-2 text-sm leading-relaxed text-[#C5BDB3]">{it.description}</p>}
        </li>
      ))}
    </ol>
  );
};

const About = ({ data }) => {
  const { profile } = useContent();
  return (
    <div className="space-y-7">
      <div className="flex items-end gap-5">
        {data?.portrait && (
          <div className="arch-frame h-40 w-32 shrink-0 overflow-hidden">
            <img src={resolveSrc(data.portrait)} alt={`${profile.firstName} ${profile.lastName}`} className="h-full w-full object-cover" />
          </div>
        )}
        <div>
          <h3 className="font-display text-2xl text-[#F7F4EE]">{profile.firstName} {profile.lastName}</h3>
          <p className="mt-1 text-sm text-[#E6B877]">{profile.role}</p>
          {profile.location && <p className="mt-2 flex items-center gap-1.5 text-xs text-[#C5BDB3]"><MapPin className="h-3.5 w-3.5" />{profile.location}</p>}
        </div>
      </div>
      {profile.tagline && <p className="text-base leading-relaxed text-[#F7F4EE]">{profile.tagline}</p>}
      {list(data?.paragraphs).map((p, i) => <p key={i} className="text-sm leading-relaxed text-[#C5BDB3]">{p}</p>)}
      {data?.quote && <blockquote className="border-l-2 border-[#C85A32] pl-4 font-display text-lg italic text-[#F7F4EE]">{data.quote}</blockquote>}
      <div className="grid grid-cols-2 gap-3">
        {list(data?.facts).map((f, i) => (
          <div key={i} className="tile">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#B5AB9E]">{f.label}</p>
            <p className="mt-1 font-display text-lg text-[#F7F4EE]">{f.value}</p>
          </div>
        ))}
      </div>
      {profile.availability && (
        <p className="inline-flex items-center gap-2 rounded-full border border-[#6E8B74]/50 bg-[#6E8B74]/15 px-3 py-1.5 text-xs text-[#CFE0D3]">
          <span className="h-2 w-2 animate-pulse rounded-full bg-[#8FC29B]" />{profile.availability}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        {list(profile.socials).filter((s) => s?.url).map((s) => (
          <a key={s.label} href={s.url} target="_blank" rel="noreferrer" className="chip" data-testid={`social-link-${String(s.label).toLowerCase()}`}>
            {s.label}<ArrowUpRight className="h-3.5 w-3.5" />
          </a>
        ))}
      </div>
    </div>
  );
};

const Films = ({ data, scroller }) => {
  const [current, setCurrent] = useState(null);
  const t = useT();
  const items = list(data?.items);
  if (!items.length) return <Empty />;
  const play = (f) => { setCurrent(f); scroller?.current?.scrollTo({ top: 0, behavior: "smooth" }); };
  return (
    <div className="space-y-6">
      {current ? (
        <div className="space-y-3">
          <div className="aspect-video overflow-hidden rounded-2xl bg-black ring-1 ring-[#F7F4EE]/10">
            <iframe key={current.video} data-testid="video-player-iframe" src={toEmbed(current.video)} title={current.title} className="h-full w-full"
              allow="autoplay; fullscreen; picture-in-picture; encrypted-media" allowFullScreen />
          </div>
          <h3 className="font-display text-xl text-[#F7F4EE]">{current.title} {current.year && <span className="text-sm text-[#B5AB9E]">({current.year})</span>}</h3>
          <p className="text-xs uppercase tracking-[0.18em] text-[#E6B877]">{[current.role, current.duration].filter(Boolean).join(" · ")}</p>
          <p className="text-sm leading-relaxed text-[#C5BDB3]">{current.synopsis}</p>
        </div>
      ) : (
        <p className="text-sm text-[#C5BDB3]">{t("pickFilm")}</p>
      )}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {items.map((f, i) => (
          <button key={i} data-testid={`film-thumbnail-${i}`} onClick={() => play(f)} className={`film-card group text-left ${current === f ? "is-active" : ""}`}>
            <div className="relative aspect-video overflow-hidden rounded-xl bg-[#2A2420]">
              {f.thumbnail && <img src={resolveSrc(f.thumbnail)} alt={f.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />}
              <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
              <span className="absolute bottom-3 left-3 flex h-9 w-9 items-center justify-center rounded-full bg-[#F7F4EE] text-[#12100E] transition-transform duration-300 group-hover:scale-110">
                <Play className="ml-0.5 h-4 w-4 fill-current" />
              </span>
              {f.genre && <span className="absolute right-3 top-3 rounded-full bg-black/55 px-2 py-0.5 font-mono text-[10px] uppercase tracking-widest text-[#F7F4EE]">{f.genre}</span>}
            </div>
            <p className="mt-2 font-display text-base text-[#F7F4EE]">{f.title}</p>
            <p className="text-xs text-[#B5AB9E]">{[f.year, f.duration].filter(Boolean).join(" · ")}</p>
          </button>
        ))}
      </div>
    </div>
  );
};

const Gallery = ({ data, openLightbox }) => {
  const items = list(data?.items).filter((x) => x?.image);
  if (!items.length) return <Empty />;
  const lb = items.map((x) => ({ src: resolveSrc(x.image), caption: [x.title, x.caption].filter(Boolean).join(" — ") }));
  return (
    <div className="grid grid-cols-2 gap-3">
      {items.map((it, i) => (
        <button key={i} data-testid={`gallery-item-${i}`} onClick={() => openLightbox(lb, i)} className={`group overflow-hidden rounded-xl text-left ${i % 3 === 0 ? "col-span-2" : ""}`}>
          <div className={`overflow-hidden rounded-xl ${i % 3 === 0 ? "aspect-[16/10]" : "aspect-[4/5]"}`}>
            <img src={resolveSrc(it.image)} alt={it.title || it.caption} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
          </div>
          {(it.title || it.caption) && <p className="mt-2 font-display text-sm text-[#F7F4EE]">{it.title} {it.caption && <span className="text-xs text-[#B5AB9E]">{it.title ? "· " : ""}{it.caption}</span>}</p>}
        </button>
      ))}
    </div>
  );
};

const Skills = ({ data }) => (
  <div className="space-y-8">
    {list(data?.categories).map((cat, i) => (
      <div key={i}>
        <Eyebrow>{cat.name}</Eyebrow>
        <div className="space-y-4">
          {list(cat.items).map((s, k) => (
            <div key={k}>
              <div className="mb-1.5 flex justify-between text-sm"><span className="text-[#F7F4EE]">{s.name}</span><span className="font-mono text-xs text-[#B5AB9E]">{s.level}%</span></div>
              <Bar value={s.level} />
            </div>
          ))}
        </div>
      </div>
    ))}
  </div>
);

const Languages = ({ data }) => {
  const t = useT();
  return (
  <div className="space-y-9">
    {list(data?.spoken).length > 0 && (
      <div>
        <Eyebrow>{t("spoken")}</Eyebrow>
        <div className="space-y-4">
          {list(data.spoken).map((l, i) => (
            <div key={i}>
              <div className="mb-1.5 flex justify-between text-sm"><span className="text-[#F7F4EE]">{l.name}</span><span className="text-xs text-[#C5BDB3]">{l.level}</span></div>
              <Bar value={l.value} />
            </div>
          ))}
        </div>
      </div>
    )}
    {list(data?.countries).length > 0 && (
      <div>
        <Eyebrow>{t("countries")}</Eyebrow>
        <ul className="divide-y divide-[#F7F4EE]/10">
          {list(data.countries).map((c, i) => (
            <li key={i} className="flex items-baseline justify-between gap-4 py-3">
              <div><p className="font-display text-lg text-[#F7F4EE]">{c.name}</p><p className="text-xs text-[#C5BDB3]">{c.note}</p></div>
              <span className="font-mono text-xs text-[#E6B877]">{c.year}</span>
            </li>
          ))}
        </ul>
      </div>
    )}
  </div>
  );
};

const Record = ({ r, i, open, onToggle }) => {
  const t = useT();
  const embed = spotifyEmbed(r.spotify);
  const playable = Boolean(embed || r.audio);
  return (
    <li className={`record rounded-2xl ${open ? "is-open" : ""}`}>
      <button data-testid={`record-item-${i}`} onClick={() => playable && onToggle(i)} className={`group flex w-full items-center gap-4 p-2 pr-4 text-left ${playable ? "" : "cursor-default"}`}>
        <div className="relative h-16 w-16 shrink-0">
          {r.cover ? <img src={resolveSrc(r.cover)} alt="" className="absolute inset-0 h-full w-full rounded-lg object-cover" /> : <div className="absolute inset-0 rounded-lg" style={{ background: r.color || "#C85A32" }} />}
          <div className={`vinyl absolute inset-1 rounded-full transition-transform duration-500 group-hover:translate-x-6 ${open ? "is-spinning translate-x-6" : ""}`}>
            <span className="absolute inset-[38%] rounded-full" style={{ background: r.color || "#C85A32" }} />
          </div>
        </div>
        <div className={`min-w-0 flex-1 transition-transform duration-500 group-hover:translate-x-6 ${open ? "translate-x-6" : ""}`}>
          <p className="truncate font-display text-base text-[#F7F4EE]">{r.album}</p>
          <p className="text-xs text-[#C5BDB3]">{[r.artist, r.year].filter(Boolean).join(" · ")}</p>
        </div>
        {playable && (
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-[#F7F4EE]/20 text-[#F7F4EE]">
            {open ? <X className="h-3.5 w-3.5" /> : <Play className="ml-0.5 h-3.5 w-3.5 fill-current" />}
          </span>
        )}
      </button>
      {open && (
        <div className="px-2 pb-3" data-testid={`record-player-${i}`}>
          {embed ? <iframe title={`${t("excerpt")} — ${r.album}`} src={embed} className="h-[152px] w-full rounded-xl" allow="autoplay; clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" />
            : <audio controls autoPlay src={resolveSrc(r.audio)} className="w-full" />}
        </div>
      )}
    </li>
  );
};

const Chips = ({ title, items }) => (list(items).length ? (
  <div><Eyebrow>{title}</Eyebrow><div className="flex flex-wrap gap-2">{list(items).map((g, i) => <span key={i} className="chip">{g}</span>)}</div></div>
) : null);

const Music = ({ data }) => {
  const [open, setOpen] = useState(null);
  const t = useT();
  return (
    <div className="space-y-8">
      {data?.intro && <p className="text-sm leading-relaxed text-[#C5BDB3]">{data.intro}</p>}
      <ul className="space-y-2">
        {list(data?.records).map((r, i) => <Record key={i} r={r} i={i} open={open === i} onToggle={(k) => setOpen((o) => (o === k ? null : k))} />)}
      </ul>
      <Chips title={t("genres")} items={data?.genres} />
      <Chips title={t("inspirations")} items={data?.artists} />
    </div>
  );
};

const Cards = ({ data }) => {
  const items = list(data?.items);
  if (!items.length) return <Empty />;
  return (
    <div className="grid gap-3">
      {items.map((s, i) => (
        <div key={i} className="tile">
          <div className="flex items-baseline justify-between gap-3">
            <h3 className="font-display text-lg text-[#F7F4EE]">{s.title}</h3>
            {s.subtitle && <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-[#E6B877]">{s.subtitle}</span>}
          </div>
          {s.description && <p className="mt-1 text-sm text-[#C5BDB3]">{s.description}</p>}
        </div>
      ))}
    </div>
  );
};

const Text = ({ data }) => {
  const ps = list(data?.paragraphs);
  if (!ps.length) return <Empty />;
  return <div className="space-y-4">{ps.map((p, i) => <p key={i} className="text-sm leading-relaxed text-[#D9CFC2]">{p}</p>)}</div>;
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = ({ data }) => {
  const { profile } = useContent();
  const t = useT();
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: "", email: "", message: "" });
      toast.success(t("sentToast"));
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast.error(Array.isArray(d) ? d[0]?.msg : d || t("sendError"));
    } finally {
      setSending(false);
    }
  };
  return (
    <div className="space-y-7">
      {data?.intro && <p className="text-sm leading-relaxed text-[#C5BDB3]">{data.intro}</p>}
      <div className="grid gap-2 sm:grid-cols-2">
        {profile.email && <a href={`mailto:${profile.email}`} className="tile flex items-center gap-3 text-sm text-[#F7F4EE]" data-testid="contact-email-link"><Mail className="h-4 w-4 text-[#E69C24]" />{profile.email}</a>}
        {profile.phone && <a href={`tel:${String(profile.phone).replace(/\s/g, "")}`} className="tile flex items-center gap-3 text-sm text-[#F7F4EE]" data-testid="contact-phone-link"><Phone className="h-4 w-4 text-[#E69C24]" />{profile.phone}</a>}
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="contact-form">
        <label className="field"><span>{t("yourName")}</span><input data-testid="contact-form-name-input" required minLength={2} value={form.name} onChange={set("name")} placeholder="Agnès Varda" /></label>
        <label className="field"><span>{t("yourEmail")}</span><input data-testid="contact-form-email-input" required type="email" value={form.email} onChange={set("email")} placeholder={t("emailPh")} /></label>
        <label className="field"><span>{t("yourMessage")}</span><textarea data-testid="contact-form-message-input" required minLength={5} rows={5} value={form.message} onChange={set("message")} placeholder={t("messagePh")} /></label>
        <button data-testid="contact-form-submit-button" disabled={sending} className="btn-gold w-full justify-center">
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}{sending ? t("sending") : t("send")}
        </button>
        {sent && <p data-testid="contact-form-success" className="text-center text-sm text-[#CFE0D3]">{t("sent")}</p>}
      </form>
    </div>
  );
};

const linkHref = (url) => (/^https?:\/\//i.test(url) ? url : `https://${url}`);

const Credits = ({ data }) => {
  const people = list(data?.people).filter((p) => p?.name);
  return (
    <div className="space-y-7">
      {data?.aiNote && (
        <div className="flex gap-3 rounded-2xl border border-[#E69C24]/40 bg-[#E69C24]/10 p-4" data-testid="credits-ai-note">
          <Sparkles className="mt-0.5 h-4 w-4 shrink-0 text-[#E69C24]" />
          <p className="text-sm leading-relaxed text-[#F2E6D2]">{data.aiNote}</p>
        </div>
      )}
      {data?.intro && <p className="text-sm leading-relaxed text-[#D9CFC2]">{data.intro}</p>}
      {people.map((p, i) => (
        <div key={i} className="tile flex gap-4" data-testid={`credits-person-${i}`}>
          {p.photo && <img src={resolveSrc(p.photo)} alt={p.name} className="h-16 w-16 shrink-0 rounded-full object-cover" />}
          <div className="min-w-0 space-y-2">
            {p.role && <p className="font-mono text-[11px] uppercase tracking-[0.2em] text-[#E6B877]">{p.role}</p>}
            <h3 className="font-display text-xl text-[#F7F4EE]">{p.name}</h3>
            {p.description && <p className="text-sm leading-relaxed text-[#C5BDB3]">{p.description}</p>}
            <div className="flex flex-wrap gap-2 pt-1">
              {list(p.links).filter((l) => l?.url).map((l, j) => (
                <a key={j} href={linkHref(l.url)} target="_blank" rel="noreferrer" className="chip" data-testid={`credits-link-${i}-${j}`}>
                  {l.label || l.url}<ArrowUpRight className="h-3.5 w-3.5" />
                </a>
              ))}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export const TYPE_VIEWS = { about: About, films: Films, gallery: Gallery, timeline: Timeline, skills: Skills, languages: Languages, music: Music, cards: Cards, contact: Contact, text: Text, credits: Credits };
